// Turns the backend's candidate cluster into the sheet's record pool.
//
// Two payloads meet here. The dedupe endpoint knows WHICH records are suspected of
// being the same well and why; the wells query knows what each of those records
// actually says. Neither alone fills a row of the suggestions table, so this module is
// the join — kept pure so the mapping is testable without a server.

import { formatNumber } from 'utils/number';
import { WELL_STATUS_LABELS, type WellStatusToken } from 'types/well-workspace';
import type {
  WellboreMapData,
  WellWithWellboresResult,
} from 'types/api-responses';
import type {
  DedupeMatchRule,
  DuplicateWellCluster,
  DuplicateWellMember,
} from './types';
import type {
  DupReason,
  DupReasonKind,
  WellCandidate,
} from './consolidation-utils';

/** The detector's rule names, as the sheet's tags know them. */
const REASON_KIND: Record<DedupeMatchRule, DupReasonKind> = {
  name_surface_hole: 'name-surface',
  surface_bottom_hole: 'surface-bottom',
};

const REASON_LABEL: Record<DupReasonKind, string> = {
  'name-surface': 'Name + surface hole',
  'surface-bottom': 'Surface + bottom hole',
};

/**
 * The decimals each rule matches coordinates on (the detector's `COORD_PRECISION` and
 * `SURFACE_BOTTOM_PRECISION`), so the evidence is shown at the precision it matched.
 */
const COORD_PRECISION: Record<DedupeMatchRule, number> = {
  name_surface_hole: 3,
  surface_bottom_hole: 4,
};

const PRECISION_WORD: Record<number, string> = { 3: 'three', 4: 'four' };

/**
 * A lat/lon pair as the detector compared it.
 * @param {number} precision The decimals the rule matched on.
 * @param {[number, number] | undefined} pair The [lat, lon] pair, if the rule carried one.
 * @return {string} The rounded pair, or an empty string when there is none.
 */
const coords = (precision: number, pair?: [number, number]): string => {
  if (!pair) return '';
  /**
   * One coordinate at the detector's own precision.
   * @param {number} value The degree value.
   * @return {string} The rounded value, unlocalised.
   */
  const at = (value: number) =>
    formatNumber(value, precision, precision, 'decimal', false);
  return `${at(pair[0])}, ${at(pair[1])}`;
};

/**
 * Why this record was tied to the cluster, worded per rule.
 *
 * The detector records the evidence twice — once on the cluster (`metadata`, what the
 * whole group shares) and once per member (`matchLinks`, what this record contributed).
 * The member's copy is preferred and the cluster's stands in, so a member written by an
 * older run still reads.
 * @param {DedupeMatchRule} rule The rule being explained.
 * @param {DuplicateWellCluster} cluster The cluster the record belongs to.
 * @param {DuplicateWellMember} member The record.
 * @return {string} The reason's detail line.
 */
const detailFor = (
  rule: DedupeMatchRule,
  cluster: DuplicateWellCluster,
  member: DuplicateWellMember
): string => {
  const links = member.matchLinks ?? {};
  const precision = COORD_PRECISION[rule];
  const matched = `matched to ${PRECISION_WORD[precision]} decimals.`;

  if (rule === 'name_surface_hole') {
    const name = links.name ?? cluster.metadata?.name ?? '';
    const surface = coords(
      precision,
      links.surface_hole ?? cluster.metadata?.surface_hole
    );
    return (
      `Same well name${name ? ` (${name})` : ''} and surface hole` +
      `${surface ? ` (${surface})` : ''}, ${matched}`
    );
  }

  const surface = coords(
    precision,
    links.surface_hole ?? cluster.metadata?.surface_hole
  );
  const bottom = coords(
    precision,
    links.bottom_hole ?? cluster.metadata?.bottom_hole
  );
  return (
    `Same surface hole${surface ? ` (${surface})` : ''} and bottom hole` +
    `${bottom ? ` (${bottom})` : ''}, ${matched}`
  );
};

/**
 * Every rule that tied a record to its cluster: the rule the cluster is filed under,
 * then any the detector folded in when it collapsed identical member sets.
 * @param {DuplicateWellCluster} cluster The cluster the record belongs to.
 * @param {DuplicateWellMember} member The record.
 * @return {DupReason[]} One reason per rule, primary first.
 */
export const buildReasons = (
  cluster: DuplicateWellCluster,
  member: DuplicateWellMember
): DupReason[] => {
  const also = cluster.metadata?.also_matched_rules ?? [];
  const rules = [cluster.matchRule, ...also].filter(
    (rule, index, all) => REASON_KIND[rule] && all.indexOf(rule) === index
  );

  return rules.map((rule) => {
    const kind = REASON_KIND[rule];
    return {
      kind,
      label: REASON_LABEL[kind],
      detail: detailFor(rule, cluster, member),
    };
  });
};

/**
 * The bore a record's map trace and hole flags come from: the producing bore, or the
 * first one when none is classified as producing (an estimated vertical, typically).
 * @param {WellWithWellboresResult | undefined} detail The wells-query row.
 * @return {WellboreMapData | null} The bore, or null when the well has no geometry.
 */
const producingBore = (
  detail: WellWithWellboresResult | undefined
): WellboreMapData | null => {
  const bores = detail?.wellBores ?? [];
  return bores.find((bore) => bore.type === 'producing') ?? bores[0] ?? null;
};

/**
 * The record pool for the clusters a well belongs to: one candidate per record, the
 * viewed well first.
 *
 * A well can sit in more than one cluster — each rule groups its own member set, and
 * only identical sets are collapsed — and the per-well count tallies peers across all
 * of them, so the pool is their union. A record in several clusters keeps every reason
 * that tied it to the viewed well, and is a suggested survivor if any cluster names it.
 *
 * The viewed well leads because its order fixes the map's trajectory colours and the
 * table reads top-down from the record being matched against. It is not necessarily
 * the backend's suggested survivor — on the Map Query page the operator opens the sheet
 * on whichever row they clicked.
 * @param {DuplicateWellCluster[]} clusters The candidate clusters holding the anchor.
 * @param {WellWithWellboresResult[]} details The wells-query rows for their members.
 * @param {string} anchorUwi The well the sheet was opened on.
 * @return {WellCandidate[]} The pool, anchor first.
 */
export const buildPoolFromClusters = (
  clusters: DuplicateWellCluster[],
  details: WellWithWellboresResult[],
  anchorUwi: string
): WellCandidate[] => {
  const byUwi = new Map(details.map((detail) => [detail.uwi, detail]));

  // Each record once, in first-seen order, with its entry in every cluster it is in.
  const memberships = new Map<
    string,
    { cluster: DuplicateWellCluster; member: DuplicateWellMember }[]
  >();
  clusters.forEach((cluster) => {
    cluster.members.forEach((member) => {
      const seen = memberships.get(member.uwi);
      if (seen) seen.push({ cluster, member });
      else memberships.set(member.uwi, [{ cluster, member }]);
    });
  });

  const candidates = [...memberships.entries()].map(([uwi, entries]) => {
    const detail = byUwi.get(uwi);
    const bore = producingBore(detail);
    const isAnchor = uwi === anchorUwi;
    const status = detail?.status as WellStatusToken | undefined;
    const reasons = isAnchor
      ? []
      : entries
          .flatMap(({ cluster, member }) => buildReasons(cluster, member))
          .filter(
            (reason, index, all) =>
              all.findIndex((r) => r.kind === reason.kind) === index
          );

    return {
      rowId: uwi,
      uwi,
      // The producing bore's UWI14, falling back to the permit-header UWI14 when the well has
      // no real bore (stick-only or none) — the dedup member carries both. Null only when it
      // has no permit either. has_geometry is the backend's authoritative real-bore flag.
      uwi14: bore?.uwi14 ?? entries[0].member.permitUwi14 ?? null,
      hasGeometry: entries[0].member.hasGeometry,
      wellName:
        entries.find(({ member }) => member.wellName)?.member.wellName ??
        detail?.name ??
        uwi,
      operator: detail?.currentOperator ?? 'Unknown operator',
      wellStatus: status ? (WELL_STATUS_LABELS[status] ?? status) : '—',
      measuredDepth: detail?.measuredDepthFeet ?? 0,
      tvd: detail?.trueVerticalDepthFeet ?? null,
      // No endpoint serves a perforated lateral length alongside the wells query; the
      // table renders an em dash for it rather than inventing one.
      perfLateral: null,
      spudDate: detail?.spudDate ?? null,
      firstProductionDate: detail?.firstProductionDate ?? null,
      hasSurfaceHole: Boolean(bore?.surfaceHole ?? detail?.surfaceHole),
      hasBottomHole: Boolean(bore?.bottomHole),
      // The survey summary (source, station count, MD range) has no source in any
      // current payload; the row's survey line simply does not render.
      survey: null,
      reasons,
      isAnchor,
      isSuggestedSurvivor: entries.some(
        ({ member }) => member.role === 'survivor'
      ),
    };
  });

  return [
    ...candidates.filter((candidate) => candidate.isAnchor),
    ...candidates.filter((candidate) => !candidate.isAnchor),
  ];
};

/**
 * A single pool candidate from a wells-query row, for a well pulled in from the catalog via
 * "Add wells to reconcile" rather than suggested by the detector. It carries no match reasons and is
 * never the anchor or a suggested survivor — the operator checks it into the reconcile by hand. Mirrors
 * the per-candidate field mapping in buildPoolFromClusters (minus the cluster-membership fields).
 * @param {WellWithWellboresResult} detail The wells-query row.
 * @return {WellCandidate} The candidate, keyed on its uwi10.
 */
export const buildCandidateFromDetail = (
  detail: WellWithWellboresResult
): WellCandidate => {
  const bore = producingBore(detail);
  const status = detail.status as WellStatusToken | undefined;
  return {
    rowId: detail.uwi,
    uwi: detail.uwi,
    // A catalog-added well has no dedup member, so there is no permit fallback here; its UWI14
    // is the producing bore's or none. has_geometry is derived locally from a REAL bore being present.
    // The 2D endpoint keys that on uwi14: every surveyed/estimated bore carries one, while a well with
    // no drawable bores is served a single placeholder bore with uwi14 null (`_deviation_survey_2d`
    // emits `wellBores([{}])` with the well's surface-hole/stick but a null uwi14). A stick is not the
    // signal — the placeholder still carries the well's auto-stick, and a real surveyed bore carries one
    // too — so this reads "No" for stick-only and no-geometry wells, matching the backend has_geometry
    // flag used for dedup members.
    uwi14: bore?.uwi14 ?? null,
    hasGeometry: (detail.wellBores ?? []).some(
      (wellbore) => (wellbore?.uwi14 ?? null) !== null
    ),
    wellName: detail.name ?? detail.uwi,
    operator: detail.currentOperator ?? 'Unknown operator',
    wellStatus: status ? (WELL_STATUS_LABELS[status] ?? status) : '—',
    measuredDepth: detail.measuredDepthFeet ?? 0,
    tvd: detail.trueVerticalDepthFeet ?? null,
    perfLateral: null,
    spudDate: detail.spudDate ?? null,
    firstProductionDate: detail.firstProductionDate ?? null,
    hasSurfaceHole: Boolean(bore?.surfaceHole ?? detail.surfaceHole),
    hasBottomHole: Boolean(bore?.bottomHole),
    survey: null,
    reasons: [],
    isAnchor: false,
    isSuggestedSurvivor: false,
    isAdded: true,
  };
};

/**
 * Map geometry for a pool, keyed by row id — the shape the inset map reads.
 * @param {WellWithWellboresResult[]} details The wells-query rows for the pool.
 * @return {Record<string, WellboreMapData>} Geometry per row id, omitting wells with none.
 */
export const buildGeometryFromDetails = (
  details: WellWithWellboresResult[]
): Record<string, WellboreMapData> => {
  const geometry: Record<string, WellboreMapData> = {};

  details.forEach((detail) => {
    const bore = producingBore(detail);
    if (!bore) return;
    // A permit or pre-drill well has no drilled trace; its well-level surface hole is
    // the only thing that can be placed, so it stands in for the bore's.
    const surfaceHole = bore.surfaceHole ?? detail.surfaceHole ?? null;
    if (!surfaceHole && !bore.stick && !bore.survey) return;

    geometry[detail.uwi] = { ...bore, surfaceHole };
  });

  return geometry;
};
